import { Autocomplete, CircularProgress, Popper, TextField } from '@mui/material';
import { useMap } from 'es-map-widget';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { BASE_COLORS } from '../../../constants';
import { api as API, useApi } from '../../../hooks/useApi';
import useDebounce from '../../../hooks/useDebounce';
import useNotify from '../../../hooks/useNotify';
import { useAppControl } from '../../../providers/AppContext';
import { useLayout } from '../../../providers/LayoutContext';

const DroughtWarningSearchPopper = forwardRef(function DroughtWarningSearchPopper(props, ref) {
    return (
        <Popper
            {...props}
            ref={ref}
            sx={[
                {
                    mt: 0.5,
                    borderRadius: 1,
                    '& .MuiAutocomplete-paper': {
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: `1px solid ${BASE_COLORS.BACKGROUND_LIGHT_GRAY}`,
                        boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)',
                    },
                },
                props.sx,
            ]}
        />
    );
});

const MEPAR_GROUP = 'MePAR blokkok';
const CITY_GROUP = 'Települések';

// A Magyarországot lefedő extent, a backend a névre keresésnél figyelmen kívül hagyja
const COUNTRY_EXTENT = [271973, -201809, 1103127, 605541];

const toMeparOption = (block) => ({
    type: 'mepar',
    id: `mepar-${block.meparCode}`,
    label: block.meparCode,
    group: MEPAR_GROUP,
    meparCode: block.meparCode,
});

const toCityOption = (city) => ({
    type: 'city',
    id: `city-${city.id}`,
    label: city.text,
    group: CITY_GROUP,
    name: city.text,
});

const DroughtWarningSearch = ({ onSelectMepar = () => {}, selectedMeparCode = '' }) => {
    const map = useMap();
    const { year } = useAppControl();
    const { matchesLgUp } = useLayout();
    const { notifyError } = useNotify();

    const [searchInput, setSearchInput] = useState(selectedMeparCode);
    const [searchPhrase, setSearchPhrase] = useState(selectedMeparCode);
    const [selectedCity, setSelectedCity] = useState(null);
    const [cityLoading, setCityLoading] = useState(false);

    useEffect(() => {
        if (!selectedMeparCode) {
            return;
        }

        setSelectedCity(null);
        setSearchInput(selectedMeparCode);
        setSearchPhrase(selectedMeparCode);
    }, [selectedMeparCode]);

    useDebounce(() => setSearchPhrase(searchInput.trim()), [searchInput], 300);

    const [blockResult, blocksLoading] = useApi(
        (api, params) =>
            api.blocks.searchBlocks(
                {
                    phrase: encodeURIComponent(searchPhrase) || undefined,
                    limit: 10,
                },
                params
            ),
        [searchPhrase],
        (data) => data,
        !searchPhrase
    );

    const [searchResult, searchLoading] = useApi(
        (api, params) =>
            api.search.searchAll({ phrase: searchPhrase || undefined, limit: 10, year }, params),
        [searchPhrase, year],
        (data) => data,
        !searchPhrase
    );

    const loading = blocksLoading || searchLoading;

    const options = useMemo(() => {
        const meparOptions = (blockResult || []).map(toMeparOption);
        const cityOptions = (searchResult || [])
            .filter((item) => item.type === 'city')
            .map(toCityOption);

        return [...meparOptions, ...cityOptions];
    }, [blockResult, searchResult]);

    const selectedOption = useMemo(() => {
        if (selectedCity) {
            return selectedCity;
        }

        if (!selectedMeparCode) {
            return null;
        }

        return (
            options.find((option) => option.meparCode === selectedMeparCode) ||
            toMeparOption({ meparCode: selectedMeparCode })
        );
    }, [options, selectedCity, selectedMeparCode]);

    // Településre keresve csak odanavigálunk, a cellák kiválasztását nem módosítjuk
    const navigateToCity = useCallback(
        (cityOption) => {
            setCityLoading(true);
            API.layers
                .listCities({ names: [cityOption.name], extent: COUNTRY_EXTENT })
                .then(({ data }) => {
                    const [city] = data || [];

                    if (city?.geom) {
                        map.zoomToGeomsExtent(
                            [{ geom: city.geom }],
                            matchesLgUp ? {} : { padding: [50, 50, 50, 50] }
                        );
                    }
                })
                .catch((error) => notifyError(error))
                .finally(() => setCityLoading(false));
        },
        [map, matchesLgUp, notifyError]
    );

    return (
        <Autocomplete
            fullWidth
            options={options}
            value={selectedOption}
            inputValue={searchInput}
            loading={loading || cityLoading}
            PopperComponent={DroughtWarningSearchPopper}
            filterOptions={(x) => x}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label || ''}
            onChange={(_, newValue) => {
                setSearchInput(newValue?.label || '');
                setSearchPhrase(newValue?.label || '');

                if (newValue?.type === 'city') {
                    setSelectedCity(newValue);
                    navigateToCity(newValue);
                    return;
                }

                setSelectedCity(null);
                onSelectMepar(newValue);
            }}
            onInputChange={(_, newInputValue, reason) => {
                if (
                    reason === 'reset' &&
                    selectedMeparCode &&
                    newInputValue === selectedMeparCode &&
                    searchInput !== selectedMeparCode
                ) {
                    return;
                }

                setSearchInput(newInputValue);

                if (reason === 'clear') {
                    setSelectedCity(null);
                    onSelectMepar(null);
                }
            }}
            noOptionsText={searchInput ? 'Nincs találat' : 'Adja meg a MePAR kódot vagy települést'}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="MePAR kód vagy település"
                    fullWidth
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {cityLoading ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            sx={{
                width: { xs: '100%', sm: 300, lg: 360 },
                minWidth: 0,
                backgroundColor: '#fff',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                },
            }}
        />
    );
};

export default DroughtWarningSearch;
